'use client';

import React from 'react';

import ImageProxy from '@next-image-proxy';

import { ExternalLink } from '@uikit';

import useDisableRedirectOnLogin from '@shared/hooks/useDisableRedirectOnLogin';

import CustomStoryBanner from '../CustomStoryBanner';
import CustomStoryLayout from '../CustomStoryLayout';
import CustomStoryStatCardsList from '../CustomStoryStatCardsList';
import CampaignLeaderboard from '../ui/CampaignLeaderboard/CampaignLeaderboard';
import { investors, stats } from './data';
import StepToJoinStabullCampaign from './StepToJoinStabullCampaign';

const StabullCampaignStory = () => {
  useDisableRedirectOnLogin();

  return (
    <CustomStoryLayout
      title="Stabull Project Overview and Campaign Details"
      banner={
        <CustomStoryBanner
          alt="Stabull Road-to-IDO $200,000 in $STABUL Token Rewards. Magic Square. Stabull"
          desktop={{
            src: '/store/stories/stabull/stabull-story-banner-desktop.png',
            width: 1742,
            height: 296,
            quality: 80,
          }}
          tablet={{
            src: '/store/stories/stabull/stabull-story-banner-desktop.png',
            width: 1742,
            height: 296,
            quality: 80,
          }}
          mobile={{
            src: '/store/stories/stabull/stabull-story-banner-desktop.png',
            width: 1742,
            height: 296,
            quality: 80,
          }}
        />
      }
    >
      <div className="story-prose">
        <h2>Meet Stabull</h2>

        <p>An AMM optimized for stablecoins and RWAs. Swap, provide liquidity & earn.</p>

        <h2>Introduction</h2>

        <p>
          As the global financial landscape evolves, stablecoins and Real World Assets (RWAs) are
          quickly becoming a central focus in the cryptocurrency sector. Over the next 3-9 months,
          hundreds of millions of investments will transition from development to deployment,
          tokenizing assets such as gold, oil, stocks, commodities, bonds, and more. Stabull is
          uniquely positioned to meet this emerging demand, providing a decentralized exchange
          specifically built for FX and commodities, designed to handle the unique requirements of
          these markets.
        </p>

        <ImageProxy
          src="/store/stories/stabull/about-stabull.png"
          width={1162}
          height={653}
          sizes="100vw"
          quality={100}
          alt="Stabull is stablecoin DEX on Etherium and Polygon"
        />

        <ImageProxy
          className="mt-10"
          src="/store/stories/stabull/stabull-how-it-works.png"
          width={1162}
          height={653}
          sizes="100vw"
          quality={100}
          alt="Stabull - How it Works"
        />

        <h2>Problem and Solution</h2>

        <ul className="list-none pl-0">
          <li className="mb-3">
            <b>Problem:</b> Current decentralized exchanges fail to address the growing need for
            stablecoin and RWA markets. Users face high trading costs, poor capital efficiency, and
            a lack of tailored features for FX swaps.
          </li>
          <li>
            <b>Solution:</b> Stabull offers a specialized Automated Market Maker (AMM) optimized for
            stablecoins and RWAs, using oracle-informed liquidity to dynamically adjust around key
            FX pairs, like EUR/USD. This allows for low-slippage swaps and capital-efficient
            liquidity provision.
          </li>
        </ul>

        <h2>Key Features</h2>

        <ul>
          <li>24/7 permissionless access</li>
          <li>Instant transaction finality and settlement</li>
          <li>Trustless and transparent operations</li>
          <li>Low execution costs</li>
        </ul>

        <div className="flex justify-center mb-10">
          <ExternalLink
            href="https://docs.stabull.finance"
            className="text-primary-50 button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-[32px] gap-2 underline font-medium"
          >
            Learn More
          </ExternalLink>
        </div>

        <h2>Unique Value Proposition</h2>

        <p className="mt-2 mb-4">
          Stabull operates as a 4th-generation AMM/DEX, generating revenue through transaction fees
          on swaps and liquidity provision. The business model is centered around a self-balancing
          ecosystem where more liquidity attracts larger swaps, which generates more fees, leading
          to higher APY for liquidity providers.
        </p>

        <p className="mt-2 mb-2">
          Stabull's live dApp is simple but highly scalable to the Trillion Dollar Stable/RWA
          sector, which is currently on an exponential growth trajectory. Competitors are not
          designed to cater to FX and commodities markets. Stabull's unique advantage lies in its
          capital efficiency and its specialization for Stablecoins and RWAs, setting it apart from
          other platforms.
        </p>

        <h2>Stabull Investors</h2>

        <ul className="story-prose-none grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {investors.map(({ name, image }) => {
            return (
              <li
                key={name}
                className="overflow-hidden rounded-lg flex items-center justify-center bg-neutral-50 h-20"
              >
                <ImageProxy
                  width={557}
                  height={80}
                  alt={name}
                  src={image.url}
                  className="w-auto max-w-full h-auto max-h-full"
                />
              </li>
            );
          })}
        </ul>

        <div className="flex justify-center mt-10">
          <ExternalLink
            href="https://docs.stabull.finance/additional/investors"
            className="text-primary-50 button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-[32px] gap-2 underline font-medium"
          >
            Learn More
          </ExternalLink>
        </div>

        <h2>Token Utility / Tokenomics</h2>

        <ul className="list-none pl-0">
          <li className="mb-4">
            <b>Liquidity Mining:</b> 30% of the token supply and 70% of swap fees are distributed to
            liquidity providers as $STABUL tokens.
          </li>
          <li>
            <b>Governance:</b> $STABUL token holders have voting rights, allowing them to influence
            the allocation of liquidity mining rewards, pool APY, and protocol upgrades.
          </li>
        </ul>

        <h2>Deflationary Mechanism</h2>

        <p className="mt-0">
          The $STABUL token has a fixed supply of 10M, ensuring its value accrues rather than
          dilutes. Stabull's protocol fees are designed to buy $STABUL off the market and split
          revenues in a deflationary model. The starting composition allocates 5% of all fees to the
          Insurance Fund, while 95% of the fees are used to buy back $STABUL and distribute it as
          follows:
        </p>

        <ul>
          <li>
            5% to the Treasury, which buys $STABUL and converts it to non-sellable POL tokens
            (stake-only).
          </li>
          <li>20% towards POL stake-only pools.</li>
          <li>70% towards topping up the long-term liquidity mining program.</li>
        </ul>

        <p className="mt-0">
          This liquidity mining program is structured to encourage early participation and drive
          market share, similar to how early tech companies offer incentives for market penetration.
          In 2025, when the Voting Portal / DAO is launched, $STABUL holders will be able to vote
          quarterly on the fee distribution and APY emission across pools. For example, token
          holders can vote to allocate a larger percentage of fees to the Insurance Fund or boost
          APY for specific liquidity pools for a set period. This introduces competition among token
          holders for governance and staking rewards.
        </p>

        <h2>Tokenomics</h2>

        <ImageProxy
          className="mt-10"
          src="/store/stories/stabull/tokenomics.png"
          width={3486}
          height={1816}
          sizes="100vw"
          quality={100}
          alt="Tokenomics"
        />

        <div className="flex justify-center my-10">
          <ExternalLink
            href="https://docs.stabull.finance/ecosystem/tokenomics"
            className="text-primary-50 button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-[32px] gap-2 underline font-medium"
          >
            Learn More
          </ExternalLink>
        </div>

        <p className="mt-0">
          By continuously buying $STABUL tokens off the market and offering governance rights to
          holders, the system fosters ongoing demand. As the Stabull Finance platform scales, it can
          handle billions in daily volume without encountering the scaling challenges faced by
          legacy services.
        </p>

        <h2>Campaign Details</h2>

        <p className="mt-0">
          The Stabull Road-to-IDO campaign offers a prize pool of $200,000 worth of $STABUL tokens.
          Users will be rewarded based on XP collected during the campaign, with rewards distributed
          proportionally according to their participation and activity on the Stabull platform.
        </p>

        <CustomStoryStatCardsList items={stats} className="mb-10" />

        <h2>Disclaimer</h2>

        <p>
          All information provided above has been supplied by Stabull. Magic Square encourages users
          to conduct their own research. This information should not be construed as financial
          advice.
        </p>

        <StepToJoinStabullCampaign />

        <h2>Leaderboard</h2>

        <CampaignLeaderboard
          id="leaderboard"
          className="story-prose-none"
          campaignSlug="stabull-finance"
        />
      </div>
    </CustomStoryLayout>
  );
};

export default StabullCampaignStory;
